import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CaretDown,
  Plus,
  Receipt,
  Wallet,
} from '@phosphor-icons/react';
import {
  formatMoney,
  listFundEntries,
  listTeamFunds,
  postFundEntry,
  requestPayout,
  type FundEntry,
} from '@/api/shop';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// ShopTeam is the per-team lagkassa view (mobile-app §8.7). Top of the page:
// the balance with the SUM(ledger) calculation in plain sight so parents and
// leaders trust what they see. Below: the append-only ledger newest-first. Two
// inline actions a leader can take here: post a manual entry (expense / income
// / adjustment) and request a payout (which then queues for org-admin approval
// in the hub). Soft-delete and admin-only edits are deliberately absent — the
// ledger is immutable; corrections are new offsetting entries.

type Currency = 'SEK' | 'NOK' | 'DKK' | 'EUR';

const ENTRY_KINDS: FundEntry['kind'][] = [
  'sale',
  'fee_collected',
  'expense',
  'adjustment',
];

export function ShopTeam() {
  const { slug = '', teamID = '' } = useParams<{ slug: string; teamID: string }>();
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const qc = useQueryClient();

  const funds = useQuery({
    queryKey: ['shop', 'team-funds', slug],
    queryFn: () => listTeamFunds(token, slug),
    enabled: !!slug,
  });
  const fund = funds.data?.find((f) => f.team_id === teamID);

  const entries = useQuery({
    queryKey: ['shop', 'fund-entries', slug, teamID],
    queryFn: () => listFundEntries(token, slug, teamID, 200),
    enabled: !!slug && !!teamID,
  });

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 md:p-8">
      <header className="flex flex-col gap-1">
        <Link
          to={`/orgs/${slug}/shop`}
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" weight="bold" /> {t('shop.team.back')}
        </Link>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
          {fund?.team_name ?? t('shop.team.loading')}
        </h1>
        <p className="text-muted-foreground">{t('shop.team.subtitle')}</p>
      </header>

      <section className="flex flex-col gap-2 rounded-2xl border border-border bg-card p-6 shadow-sm">
        <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('shop.team.balanceLabel')}
        </p>
        <p className="font-heading text-4xl font-extrabold tracking-tight">
          {fund ? formatMoney(fund.balance_minor, fund.currency || 'SEK') : '—'}
        </p>
        <p className="text-sm text-muted-foreground">
          {t('shop.team.balanceFooter', { count: fund?.entries ?? 0 })}
        </p>
      </section>

      <Actions
        slug={slug}
        teamID={teamID}
        token={token}
        currency={(fund?.currency as Currency | undefined) ?? 'SEK'}
        balance={fund?.balance_minor ?? 0}
        onChange={() => {
          void qc.invalidateQueries({ queryKey: ['shop', 'fund-entries', slug, teamID] });
          void qc.invalidateQueries({ queryKey: ['shop', 'team-funds', slug] });
          void qc.invalidateQueries({ queryKey: ['shop', 'payouts', slug] });
        }}
      />

      <section className="flex flex-col gap-2">
        <h2 className="font-heading text-xl font-bold">{t('shop.team.ledgerTitle')}</h2>
        {entries.isLoading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : (entries.data?.length ?? 0) === 0 ? (
          <EmptyState
            icon={<Wallet weight="duotone" />}
            title={t('shop.team.ledgerEmptyTitle')}
            body={t('shop.team.ledgerEmptyBody')}
          />
        ) : (
          <ul className="divide-y divide-border rounded-2xl border border-border bg-card shadow-sm">
            {(entries.data ?? []).map((e) => (
              <li key={e.id} className="flex items-center gap-3 px-4 py-3">
                <KindBadge kind={e.kind} />
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm font-semibold">
                    {t(`shop.kinds.${e.kind}`)}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {new Date(e.created_at).toLocaleString()}
                    {e.note && ` · ${e.note}`}
                  </p>
                </div>
                <div
                  className={
                    e.amount_minor < 0
                      ? 'text-right font-heading font-semibold text-destructive'
                      : 'text-right font-heading font-semibold'
                  }
                >
                  {e.amount_minor > 0 && '+'}
                  {formatMoney(e.amount_minor, e.currency)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function KindBadge({ kind }: { kind: FundEntry['kind'] }) {
  // Colored dot per kind — green for inflow, red-ish for outflow, neutral for
  // adjustments. Avoids dragging tokens into a one-off and stays inside the
  // existing palette via Tailwind's neutral classes.
  const tone =
    kind === 'sale' || kind === 'fee_collected' || kind === 'adjustment'
      ? 'bg-primary'
      : 'bg-destructive/70';
  return <span aria-hidden className={`h-2.5 w-2.5 rounded-full ${tone}`} />;
}

function Actions({
  slug,
  teamID,
  token,
  currency,
  balance,
  onChange,
}: {
  slug: string;
  teamID: string;
  token: string | null;
  currency: Currency;
  balance: number;
  onChange: () => void;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState<'none' | 'entry' | 'payout'>('none');

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {t('shop.team.actionsTitle')}
        </h2>
        <div className="flex gap-2">
          <Button
            variant={open === 'entry' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setOpen(open === 'entry' ? 'none' : 'entry')}
          >
            <Plus className="h-4 w-4" weight="bold" /> {t('shop.team.postEntry')}
            <CaretDown className="h-3 w-3" weight="bold" />
          </Button>
          <Button
            variant={open === 'payout' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setOpen(open === 'payout' ? 'none' : 'payout')}
            disabled={balance <= 0}
          >
            <Receipt className="h-4 w-4" weight="bold" /> {t('shop.team.requestPayout')}
            <CaretDown className="h-3 w-3" weight="bold" />
          </Button>
        </div>
      </div>
      {open === 'entry' && (
        <PostEntryForm
          slug={slug}
          teamID={teamID}
          token={token}
          currency={currency}
          onDone={() => {
            setOpen('none');
            onChange();
          }}
        />
      )}
      {open === 'payout' && (
        <PayoutForm
          slug={slug}
          teamID={teamID}
          token={token}
          currency={currency}
          balance={balance}
          onDone={() => {
            setOpen('none');
            onChange();
          }}
        />
      )}
    </section>
  );
}

function PostEntryForm({
  slug,
  teamID,
  token,
  currency,
  onDone,
}: {
  slug: string;
  teamID: string;
  token: string | null;
  currency: Currency;
  onDone: () => void;
}) {
  const { t } = useTranslation('common');
  const [kind, setKind] = useState<FundEntry['kind']>('expense');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  // Outflow kinds get posted as a NEGATIVE amount per §8.7. We let the user
  // type a positive number and flip the sign here so the UI matches expense-
  // entry mental model in the rest of the app.
  const outflow = kind === 'expense' || kind === 'refund' || kind === 'provider_fee';
  const post = useMutation({
    mutationFn: () => {
      const v = Math.round(parseFloat(amount.replace(',', '.')) * 100);
      const signed = outflow ? -Math.abs(v) : Math.abs(v);
      return postFundEntry(token, slug, teamID, {
        kind,
        amount_minor: signed,
        currency,
        note,
      });
    },
    onSuccess: () => onDone(),
  });

  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_1fr_2fr_auto]">
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">{t('shop.entry.kind')}</span>
        <Select value={kind} onValueChange={(v) => setKind(v as FundEntry['kind'])}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ENTRY_KINDS.map((k) => (
              <SelectItem key={k} value={k}>
                {t(`shop.kinds.${k}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">{t('shop.entry.amount', { currency })}</span>
        <Input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          placeholder="0.00"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">{t('shop.entry.note')}</span>
        <Input value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <div className="flex items-end">
        <Button
          onClick={() => post.mutate()}
          disabled={!amount.trim() || post.isPending}
        >
          {post.isPending ? t('common.loading') : t('shop.entry.submit')}
        </Button>
      </div>
      {post.error instanceof Error && (
        <p className="col-span-full text-sm text-destructive">{post.error.message}</p>
      )}
    </div>
  );
}

function PayoutForm({
  slug,
  teamID,
  token,
  currency,
  balance,
  onDone,
}: {
  slug: string;
  teamID: string;
  token: string | null;
  currency: Currency;
  balance: number;
  onDone: () => void;
}) {
  const { t } = useTranslation('common');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const req = useMutation({
    mutationFn: () =>
      requestPayout(token, slug, teamID, {
        amount_minor: Math.round(parseFloat(amount.replace(',', '.')) * 100),
        currency,
        note,
      }),
    onSuccess: () => onDone(),
  });

  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_2fr_auto]">
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">{t('shop.payouts.amount', { currency })}</span>
        <Input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          placeholder="0.00"
        />
        <span className="text-xs text-muted-foreground">
          {t('shop.payouts.maxHint', {
            amount: formatMoney(balance, currency),
          })}
        </span>
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">{t('shop.payouts.note')}</span>
        <Input value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <div className="flex items-end">
        <Button
          onClick={() => req.mutate()}
          disabled={!amount.trim() || req.isPending}
        >
          {req.isPending ? t('common.loading') : t('shop.payouts.submit')}
        </Button>
      </div>
      {req.error instanceof Error && (
        <p className="col-span-full text-sm text-destructive">{req.error.message}</p>
      )}
      <p className="col-span-full text-xs text-muted-foreground">
        {t('shop.payouts.dualControlHint')}
      </p>
    </div>
  );
}
